import { SvgIconProps } from '@mui/material';

import { navLinkEnabledByLookup } from '@/config/lookupFeatures';
import { SHRUNK_WIDTH } from '@/config/navBarWidth';
import type { HF_ROUTES } from '@/config/routes';

import { Dashboard } from '@/components/icons/Dashboard';
import { Percent } from '@/components/icons/Percent';
import { Pin2 } from '@/components/icons/Pin2';
import { ChartColumn } from '@/components/icons/ChartColumn';
import { Receipt } from '@/components/icons/Receipt';
import { Route } from '@/components/icons/Route';
import { Stations } from '@/components/icons/Stations';
import { Tag } from '@/components/icons/Tag';

export type TNavChildLink = {
  key: keyof typeof HF_ROUTES;
  title: string;
};

export type TNavLink = {
  key: keyof typeof HF_ROUTES;
  Icon: (props: SvgIconProps & { hasNotifications?: boolean }) => JSX.Element;
  title: string;
  notificationIcon?: boolean;
  children?: TNavChildLink[];
  /**
   * Other route keys that also highlight this plain item. A section with an Overview / List
   * switch (Rebates, Visits, Transactions, EV Performance) is one menu item that opens its first
   * view; its second view is a separate route, so the item lists it here to stay highlighted
   * there. `filterNavLinksByFeatures` drops the keys the partner cannot open.
   */
  activeKeys?: TNavChildLink['key'][];
};

export const TOP_NAV_LINKS: TNavLink[] = [
  {
    key: 'dashboard',
    title: 'navBar.dashboard',
    Icon: Dashboard,
  },
  {
    key: 'stations',
    title: 'navBar.stations',
    Icon: Stations,
  },
  // Rebates (MOCK, HF-5487, Figma node 27662:28642). One plain item keyed `rebatePrograms`:
  // `/rebates` has no page, so the item opens Rebate Programs. Suppliers is reached with the
  // Overview / List switch on the page (`SECTION_VIEWS`). Always visible via
  // ALWAYS_VISIBLE_NAV_KEYS because there is no backend feature for it.
  {
    key: 'rebatePrograms',
    title: 'navBar.rebates',
    Icon: Percent,
    activeKeys: ['rebateSuppliers'],
  },
  {
    key: 'productCatalog',
    title: 'navBar.productCatalog',
    Icon: Tag,
  },
  // Visits and Transactions open their Overview; their other views are reached with the switch
  // on the page (`SECTION_VIEWS`). All views share the item's lookup feature. Transactions has
  // two more views: Fuel + Store (the store / TLog list) and EV Transactions (the EV list, which
  // also needs the EV feature).
  {
    key: 'visits',
    title: 'navBar.visits',
    Icon: Route,
    activeKeys: ['visitsList'],
  },
  {
    key: 'storeTransactions',
    title: 'navBar.storeTransactions',
    Icon: Receipt,
    activeKeys: ['storeTransactionsFuelStore', 'storeTransactionsList'],
  },
  // EV Performance (Figma node 29855:97878). Opens Overview (Site Performance); EV Transactions
  // is reached with the switch on the page. Both keep their existing URLs. Unlike the items
  // above, the two views need DIFFERENT lookup features, so `filterNavLinksByFeatures` opens the
  // first allowed one (a partner with only EV Transactions lands on /transactions).
  {
    key: 'sitePerformance',
    title: 'navBar.evPerformance',
    Icon: ChartColumn,
    activeKeys: ['transactions'],
  },
];

/**
 * Menu section for a partner who has exactly one station (`soleStationId` is set).
 * Only "My Site" (Figma node 28437:53804). It is always shown: it is not gated by lookup
 * features, because the partner's one station is reachable only through this page.
 */
export const SINGLE_SITE_NAV_LINKS: TNavLink[] = [
  {
    key: 'mySite',
    title: 'navBar.mySite',
    Icon: Pin2,
  },
];

/**
 * Route keys a single-station partner may open. Settings keeps its sub-routes (members,
 * account details, general) because the Settings page registers them itself, and keeps its
 * lookup-feature gating in `AdminRoutes`. Every other URL redirects to My Site.
 */
export const SINGLE_SITE_ROUTE_KEYS: ReadonlyArray<keyof typeof HF_ROUTES> = ['mySite', 'settings'];

// Rebates is a MOCK (HF-5487) with no backend feature, so it must show for every partner
// regardless of their lookup features. Both Rebates views are listed: the menu item is keyed
// `rebatePrograms`, and `rebateSuppliers` is its active key, which `filterNavLinksByFeatures`
// also checks (dropping it would stop the item lighting up on Suppliers).
export const ALWAYS_VISIBLE_NAV_KEYS: ReadonlySet<keyof typeof HF_ROUTES> = new Set([
  'rebatePrograms',
  'rebateSuppliers',
]);

const isNavKeyVisible = (features: string[], key: keyof typeof HF_ROUTES) =>
  ALWAYS_VISIBLE_NAV_KEYS.has(key) || navLinkEnabledByLookup(features, key);

/**
 * Menu links the partner's lookup features allow.
 *
 * - No features loaded (empty list): every link, unchanged. Same fallback as `AdminRoutes`.
 * - Single link: kept when its own key is enabled.
 * - Single link with `activeKeys` (a section with an Overview / List switch): kept when ANY of
 *   `[key, ...activeKeys]` is enabled. It is returned keyed by the FIRST enabled one (the page
 *   the item opens), with `activeKeys` set to the other enabled ones. Only EV Performance needs
 *   this today (Overview: `sitePerformance`, list: `ev transactions`); the other sections' views
 *   share one feature or are always visible, so they come back unchanged in content.
 * - Group: each sub-item is gated by its own key, because sub-items can need different
 *   features (EV Performance: `sitePerformance` vs `ev transactions`). The group is kept with
 *   only the allowed sub-items, and dropped when none are allowed. Gating the group by its
 *   parent key alone would show a sub-item whose route is not registered, or hide a group
 *   the partner can partly use.
 */
export const filterNavLinksByFeatures = (links: TNavLink[], features: string[]): TNavLink[] => {
  if (features.length === 0) {
    return links;
  }

  return links.flatMap((link) => {
    if (!link.children?.length && !link.activeKeys?.length) {
      return isNavKeyVisible(features, link.key) ? [link] : [];
    }

    if (!link.children?.length) {
      const [key, ...activeKeys] = [link.key, ...(link.activeKeys ?? [])].filter((navKey) =>
        isNavKeyVisible(features, navKey),
      );
      return key ? [{ ...link, key, activeKeys }] : [];
    }

    const children = link.children.filter((child) => isNavKeyVisible(features, child.key));
    return children.length ? [{ ...link, children }] : [];
  });
};

/**
 * Whether a plain (no sub-items) menu item is highlighted for the current page.
 *
 * `activeKey` is the route key matched from the URL (undefined when no route matches). The item
 * is highlighted on its own route and on any route in its `activeKeys` (a section's second view,
 * e.g. Visits List for Visits). Groups are not handled here: they highlight from their sub-items.
 */
export const isNavLinkActive = (
  link: Pick<TNavLink, 'key' | 'activeKeys'>,
  activeKey: keyof typeof HF_ROUTES | undefined,
): boolean => {
  if (!activeKey) {
    return false;
  }

  return link.key === activeKey || Boolean(link.activeKeys?.includes(activeKey));
};

/**
 * Detail (drill-down) pages and the menu item of the list they belong to.
 *
 * What: a site's page (`stationEntity`, /stations/:id) highlights Sites (`stations`).
 * Why: kept apart from `activeKeys` on purpose. `activeKeys` also changes which page an item
 * opens (`filterNavLinksByFeatures`) and feeds the home-path fallback (`getNavLinkKeys`), and a
 * detail page needs an id, so it must never be opened from the menu or used as a home page.
 * Constraints: only add an entry whose parent key is a menu item.
 */
export const NAV_DETAIL_PAGE_PARENTS: Readonly<
  Partial<Record<keyof typeof HF_ROUTES, keyof typeof HF_ROUTES>>
> = { stationEntity: 'stations' };

/**
 * The route key the menu should treat as active for the current page.
 *
 * What: returns the parent list's key for a detail page, otherwise the key unchanged.
 * Edge case: `undefined` (no route matched the URL) stays `undefined`.
 */
export const getNavActiveKey = (
  routeKey: keyof typeof HF_ROUTES | undefined,
): keyof typeof HF_ROUTES | undefined =>
  routeKey ? NAV_DETAIL_PAGE_PARENTS[routeKey] ?? routeKey : undefined;

/**
 * Every route key the menu can open: each item's key, then its `activeKeys`, then its sub-items,
 * in menu order and without duplicates. `getDefaultHomePath` walks this list to find the first
 * page a partner can open, so the other view of a section must be included (a partner whose only
 * feature is EV Transactions must still land on /transactions).
 */
export const getNavLinkKeys = (links: TNavLink[]): Array<keyof typeof HF_ROUTES> =>
  Array.from(
    new Set(
      links.flatMap(({ key, activeKeys = [], children = [] }) => [
        key,
        ...activeKeys,
        ...children.map((child) => child.key),
      ]),
    ),
  );

export { SHRUNK_WIDTH };
export const EXTENDED_WIDTH = 240;
export const Z_INDEX = 1000;
